import { FormArray, FormGroup } from '@angular/forms';

export function markAllAsDirty(form: FormGroup | FormArray, deep = true): void {
  Object.values(form.controls).forEach((control) => {
    if (
      (control instanceof FormGroup || control instanceof FormArray) &&
      deep
    ) {
      markAllAsDirty(control);
    } else {
      control.markAsDirty();
    }
  });
  form.markAsDirty();
}

export function getErrorsPerControl(
  formGroup: FormGroup | FormArray
): Record<string, Set<string> | null> {
  const result: Record<string, Set<string> | null> = {};

  if (formGroup.errors) {
    result['self'] = new Set(Object.keys(formGroup.errors));
  }

  Object.keys(formGroup.controls).forEach((key) => {
    const control = formGroup.get(key);

    if (!control?.dirty) return;

    if (control instanceof FormGroup || control instanceof FormArray) {
      const obj = getErrorsPerControl(control);

      for (const entry of Object.entries(obj)) {
        result[`${key}.${entry[0]}`] = entry[1];
      }
      
      // Also check for errors on the group/array itself
      if (control.errors) {
        result[key] = new Set(Object.keys(control.errors));
      }
      return;
    }

    if (control?.errors) {
      result[key] = new Set(Object.keys(control.errors));

      return;
    }
    result[key] = null;
  });
  return result;
}

/**
 * This function is created due to umss file input always return an array of files
 * Normalizes an object by recursively inspecting all its properties.
 * Any property that is detected as an array of File objects will be
 * converted into a single File instance (the first element of the array).
 *
 * - Traverses objects deeply (no depth limit).
 * - Handles nested arrays and nested objects.
 * - Leaves non-File values untouched.
 *
 * @param input The object to normalize.
 * @returns A new object with all File[] properties converted to File.
 */
export function normalizeFileArrays<T>(input: T): T {
  if (input === null || typeof input !== 'object') {
    return input;
  }

  // Arrays should be mapped deeply
  if (Array.isArray(input)) {
    return input.map((item) => normalizeFileArrays(item)) as unknown as T;
  }

  // For plain objects
  const output: any = Array.isArray(input) ? [] : { ...input };

  for (const key of Object.keys(output)) {
    const value = output[key];

    // Detect File[]
    if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
      output[key] = value[0]; // Convert to File
      continue;
    }

    // Recursively process nested structures
    output[key] = normalizeFileArrays(value);
  }

  return output;
}
export function scrollToFirstInvalidControl(): void {
  setTimeout(() => {
    const el = document.querySelector(
      '.ng-invalid:not(form):not([formgroupname]):not([formarrayname])'
    ) as HTMLElement;

    if (el) {
      // Elements with display: contents (like our vertical field or input components)
      // don't have a layout box and scrollIntoView won't work on them.
      // We scroll to the first child instead.
      const target =
        window.getComputedStyle(el).display === 'contents'
          ? (el.firstElementChild as HTMLElement) || el
          : el;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Focus the input if possible
      const inputElement = target.querySelector(
        'input, select, textarea'
      ) as HTMLElement;
      if (inputElement) {
        inputElement.focus();
      }
    }
  }, 100);
}
